import { isDeckPassiveId } from '@cards/cards';

import {
  DeckApiError,
  parseDeckCreateResult,
  parseDeckLibrary,
  parseDeckPreferenceResult,
  parseDeckUuid,
  parseSavedDeckDto,
  type CreateDeckOptions,
  CONSTRUCTED_DECK_SLOT_LIMIT,
  type DeckApi,
  type DeckApiErrorCode,
  type DeckCreateSource,
  type DeckUpdateInput,
} from './api.js';

export type DeckBrowserFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface BrowserDeckApiOptions {
  readonly fetch?: DeckBrowserFetch;
  readonly generateOperationId?: () => string;
  readonly now?: () => number;
}

const ERROR_CODES: ReadonlySet<DeckApiErrorCode> = new Set([
  'ACCOUNT_REQUIRED', 'PROFILE_INCOMPLETE', 'FORBIDDEN_ORIGIN', 'DECK_NOT_FOUND',
  'DECK_REVISION_CONFLICT', 'DECK_LIMIT_REACHED',
  'OPERATION_ID_CONFLICT', 'DECK_INVALID', 'DECK_NAME_INVALID', 'INVALID_REQUEST',
  'REQUEST_TOO_LARGE', 'RATE_LIMITED', 'SERVICE_UNAVAILABLE', 'INTERNAL_ERROR', 'METHOD_NOT_ALLOWED',
  'NOT_FOUND',
]);

const ERROR_STATUS: Readonly<Record<Exclude<DeckApiErrorCode, 'INVALID_RESPONSE'>, number>> = {
  ACCOUNT_REQUIRED: 401,
  PROFILE_INCOMPLETE: 403,
  FORBIDDEN_ORIGIN: 403,
  DECK_NOT_FOUND: 404,
  DECK_REVISION_CONFLICT: 409,
  DECK_LIMIT_REACHED: 409,
  OPERATION_ID_CONFLICT: 409,
  DECK_INVALID: 400,
  DECK_NAME_INVALID: 400,
  INVALID_REQUEST: 400,
  REQUEST_TOO_LARGE: 413,
  RATE_LIMITED: 429,
  SERVICE_UNAVAILABLE: 503,
  INTERNAL_ERROR: 500,
  METHOD_NOT_ALLOWED: 405,
  NOT_FOUND: 404,
};

function mediaType(response: Response): string | undefined {
  return response.headers.get('content-type')?.split(';', 1)[0]?.trim().toLowerCase();
}

async function readJson(response: Response): Promise<unknown> {
  if (mediaType(response) !== 'application/json') {
    throw new DeckApiError('INVALID_RESPONSE', response.status);
  }
  try {
    return await response.json();
  } catch {
    throw new DeckApiError('INVALID_RESPONSE', response.status);
  }
}

function strictErrorPayload(value: unknown): {
  code: DeckApiErrorCode;
  currentRevision?: number;
} {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error('invalid error payload');
  }
  const object = value as Record<string, unknown>;
  const code = object.error;
  if (typeof code !== 'string' || !ERROR_CODES.has(code as DeckApiErrorCode)) {
    throw new Error('unknown error code');
  }
  const keys = Object.keys(object);
  if (code === 'DECK_REVISION_CONFLICT') {
    if (keys.length !== 2 || !keys.includes('error') || !keys.includes('currentRevision')
      || !Number.isSafeInteger(object.currentRevision) || (object.currentRevision as number) <= 0) {
      throw new Error('invalid revision conflict');
    }
    return { code: code as DeckApiErrorCode, currentRevision: object.currentRevision as number };
  }
  if (keys.length !== 1 || keys[0] !== 'error') throw new Error('invalid error details');
  return { code: code as DeckApiErrorCode };
}

async function throwResponseError(response: Response): Promise<never> {
  try {
    const parsed = strictErrorPayload(await readJson(response));
    if (parsed.code === 'INVALID_RESPONSE' || ERROR_STATUS[parsed.code] !== response.status) {
      throw new Error('error status mismatch');
    }
    throw new DeckApiError(parsed.code, response.status, parsed.currentRevision);
  } catch (error) {
    if (error instanceof DeckApiError) throw error;
    throw new DeckApiError('INVALID_RESPONSE', response.status);
  }
}

async function expectJson(
  response: Response,
  expectedStatuses: readonly number[],
): Promise<unknown> {
  if (!expectedStatuses.includes(response.status)) return throwResponseError(response);
  return readJson(response);
}

async function expectParsed<T>(
  response: Response,
  expectedStatuses: readonly number[],
  parser: (value: unknown) => T,
): Promise<T> {
  const value = await expectJson(response, expectedStatuses);
  try {
    return parser(value);
  } catch (error) {
    if (error instanceof DeckApiError) throw error;
    throw new DeckApiError('INVALID_RESPONSE', response.status);
  }
}

function mutationInit(
  method: 'POST' | 'PUT' | 'DELETE',
  body: unknown,
  csrfToken: string,
  signal?: AbortSignal,
): RequestInit {
  return {
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    credentials: 'same-origin',
    // Firefox can serialize Origin as `null` for a non-CORS fetch when the page
    // also uses Referrer-Policy: no-referrer. For this normal tuple-origin
    // document, CORS mode avoids that branch. Relative URLs, same-origin
    // credentials and redirect:error keep this adapter on the production origin.
    mode: 'cors',
    redirect: 'error',
    cache: 'no-store',
    body: JSON.stringify(body),
    ...(signal === undefined ? {} : { signal }),
  };
}

function readInit(signal?: AbortSignal): RequestInit {
  return {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    mode: 'same-origin',
    redirect: 'error',
    cache: 'no-store',
    ...(signal === undefined ? {} : { signal }),
  };
}

function strictOperationId(value: string): string {
  return parseDeckUuid(value, 'operationId');
}

function exactSource(source: DeckCreateSource): DeckCreateSource {
  const keys = Object.keys(source);
  if (source.kind !== 'draft'
    || keys.length !== 4 || !keys.includes('kind') || !keys.includes('name')
    || !keys.includes('cardIds') || !keys.includes('passiveId')) {
    throw new Error('draft source shape invalid');
  }
  if (typeof source.name !== 'string' || !Array.isArray(source.cardIds)
    || (source.passiveId !== null && !isDeckPassiveId(source.passiveId))) {
    throw new Error('draft source invalid');
  }
  if (source.cardIds.length > CONSTRUCTED_DECK_SLOT_LIMIT || source.cardIds.some((cardId) =>
    typeof cardId !== 'string' || cardId.length === 0 || cardId.length > 256)) {
    throw new Error('draft cardIds invalid');
  }
  return {
    kind: 'draft',
    name: source.name,
    cardIds: [...source.cardIds],
    passiveId: source.passiveId,
  };
}

/** Owner-only, same-origin HTTP adapter. The HttpOnly bearer is never exposed. */
export function createBrowserDeckApi(options: BrowserDeckApiOptions = {}): DeckApi {
  const browserFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  const generateOperationId = options.generateOperationId ?? (() => globalThis.crypto.randomUUID());
  const now = options.now ?? Date.now;
  const operationRetentionMs = 7 * 24 * 60 * 60 * 1_000;
  const failedCreateOperations = new Map<string, Readonly<{
    operationId: string;
    expiresAt: number;
  }>>();

  function createRetryKey(source: DeckCreateSource): string {
    return JSON.stringify({
      kind: source.kind,
      name: source.name.normalize('NFKC').trim(),
      cardIds: [...source.cardIds].sort(),
      passiveId: source.passiveId,
    });
  }

  function rememberFailedOperation(key: string, operationId: string): void {
    if (failedCreateOperations.size >= 32 && !failedCreateOperations.has(key)) {
      const oldest = failedCreateOperations.keys().next().value as string | undefined;
      if (oldest !== undefined) failedCreateOperations.delete(oldest);
    }
    failedCreateOperations.set(key, { operationId, expiresAt: now() + operationRetentionMs });
  }

  function retryOperationId(key: string): string | undefined {
    const remembered = failedCreateOperations.get(key);
    if (remembered === undefined) return undefined;
    if (remembered.expiresAt <= now()) {
      failedCreateOperations.delete(key);
      return undefined;
    }
    return remembered.operationId;
  }

  return {
    async list(includeArchived, signal) {
      const path = includeArchived ? '/api/decks?includeArchived=true' : '/api/decks';
      const response = await browserFetch(path, readInit(signal));
      return expectParsed(response, [200], parseDeckLibrary);
    },
    async get(deckId, signal) {
      const id = parseDeckUuid(deckId);
      const response = await browserFetch(`/api/decks/${id}`, readInit(signal));
      return expectParsed(response, [200], parseSavedDeckDto);
    },
    async create(source: DeckCreateSource, csrfToken: string, createOptions: CreateDeckOptions = {}) {
      const requestSource = exactSource(source);
      const retryKey = createRetryKey(requestSource);
      const callerOperation = createOptions.operationId;
      const rememberedOperation = callerOperation === undefined
        ? retryOperationId(retryKey)
        : undefined;
      const operationId = strictOperationId(
        callerOperation ?? rememberedOperation ?? generateOperationId(),
      );
      if (callerOperation === undefined && rememberedOperation === undefined) {
        rememberFailedOperation(retryKey, operationId);
      }
      try {
        const response = await browserFetch('/api/decks', mutationInit(
          'POST',
          { operationId, source: requestSource },
          csrfToken,
          createOptions.signal,
        ));
        const result = await expectParsed(response, [200, 201], parseDeckCreateResult);
        if ((response.status === 200) !== result.replayed) {
          throw new DeckApiError('INVALID_RESPONSE', response.status);
        }
        if (callerOperation === undefined) failedCreateOperations.delete(retryKey);
        return result;
      } catch (error) {
        // A transport/invalid-response failure may follow a committed create.
        // Retain the operation id so the next equivalent call replays safely.
        if (callerOperation === undefined
          && error instanceof DeckApiError
          && error.code !== 'INVALID_RESPONSE') {
          failedCreateOperations.delete(retryKey);
        }
        throw error;
      }
    },
    async update(deckId, input: DeckUpdateInput, csrfToken, signal) {
      const id = parseDeckUuid(deckId);
      const response = await browserFetch(`/api/decks/${id}`, mutationInit(
        'PUT',
        {
          name: input.name,
          cardIds: [...input.cardIds],
          passiveId: input.passiveId,
          expectedRevision: input.expectedRevision,
        },
        csrfToken,
        signal,
      ));
      return expectParsed(response, [200], parseSavedDeckDto);
    },
    async setArchived(deckId, archived, expectedRevision, csrfToken, signal) {
      const id = parseDeckUuid(deckId);
      const response = await browserFetch(`/api/decks/${id}/archive`, mutationInit(
        'PUT',
        { archived, expectedRevision },
        csrfToken,
        signal,
      ));
      return expectParsed(response, [200], parseSavedDeckDto);
    },
    async delete(deckId, expectedRevision, csrfToken, signal) {
      const id = parseDeckUuid(deckId);
      const response = await browserFetch(`/api/decks/${id}`, mutationInit(
        'DELETE',
        { expectedRevision },
        csrfToken,
        signal,
      ));
      if (response.status !== 204) await throwResponseError(response);
    },
    async setPreference(deckId, csrfToken, signal) {
      const id = parseDeckUuid(deckId);
      const response = await browserFetch('/api/decks/preference', mutationInit(
        'PUT',
        { deckId: id },
        csrfToken,
        signal,
      ));
      return expectParsed(response, [200], parseDeckPreferenceResult);
    },
  };
}
